import { useMemo, useState } from 'react';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ForumCard from '@/components/base/ForumCard';
import { useSeo } from '@/hooks/useSeo';
import { forums } from '@/mocks/forums';

const SITE_URL = 'https://luosai123.cn';

export default function ForumsPage() {
  const categoryNames = useMemo(
    () => Array.from(new Set(forums.map((item) => item.categoryName))),
    [],
  );
  const [activeCategory, setActiveCategory] = useState('全部');
  const [keyword, setKeyword] = useState('');

  const filtered = useMemo(() => {
    const search = keyword.trim().toLowerCase();
    return forums.filter((item) => {
      const matchCategory = activeCategory === '全部' || item.categoryName === activeCategory;
      if (!matchCategory) {
        return false;
      }
      if (!search) {
        return true;
      }
      return (
        item.name.toLowerCase().includes(search) ||
        item.description.toLowerCase().includes(search) ||
        item.tags.some((tag) => tag.toLowerCase().includes(search))
      );
    });
  }, [activeCategory, keyword]);

  const jsonLd = useMemo(
    () => [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: '论坛社区汇总',
        url: `${SITE_URL}/forums`,
        description: '收录爆料与讨论类论坛社区，按主题分类，附成员规模与开放方式说明。',
        inLanguage: 'zh-CN',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: '爆料论坛与社区列表',
        numberOfItems: forums.length,
        itemListElement: forums.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          url: item.url,
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: '论坛社区汇总', item: `${SITE_URL}/forums` },
        ],
      },
    ],
    [],
  );

  useSeo({
    title: '论坛社区汇总 - 爆料讨论版块导航｜爆料网上',
    description:
      '爆料网上论坛社区汇总收录热点讨论版、维权互助小组、城市爆料圈等讨论社区，按主题分类并标注成员规模与开放方式，帮你找到正在讨论同一件事的人。',
    keywords: '爆料论坛,爆料社区,讨论版块,维权互助,爆料网上',
    path: '/forums',
    jsonLd,
  });

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-secondary-50">
        <div className="mx-auto max-w-content px-4 py-10 md:px-6 md:py-14">
          <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '论坛社区汇总' }]} />
          <h1 className="mt-5 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
            论坛社区汇总
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            一个人整理材料容易卡住，找到正在经历同一件事的人往往能少走很多弯路。
            这里的社区按主题分类，标注了成员规模与开放方式，方便你判断是否适合加入。
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-12">
        <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索社区名称或标签"
                aria-label="搜索论坛社区"
                className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-secondary-400"
              />
            </div>
            <p className="whitespace-nowrap text-sm text-foreground-500">共 {filtered.length} 个社区</p>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {['全部', ...categoryNames].map((name) => (
              <button
                key={name}
                type="button"
                onClick={() => setActiveCategory(name)}
                className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors cursor-pointer ${
                  activeCategory === name
                    ? 'bg-secondary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                {name}
              </button>
            ))}
          </div>
        </div>

        {filtered.length > 0 ? (
          <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((forum) => (
              <ForumCard key={forum.id} forum={forum} />
            ))}
          </div>
        ) : (
          <div className="mt-5 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-chat-off-line text-2xl"></i>
            </span>
            <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-950">暂时没有匹配的社区</h2>
            <p className="mt-2 max-w-md text-sm leading-relaxed text-foreground-600">
              试试其他关键词，或者联系我们把你知道的社区补充进来。
            </p>
          </div>
        )}

        <div className="mt-10 rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
          <h2 className="font-heading text-lg font-semibold text-foreground-950">加入社区前的三点提醒</h2>
          <ul className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {[
              '先浏览置顶规则，了解哪些内容会被移出',
              '避免在公开版块直接发布身份证、住址等隐私信息',
              '遇到要求先付费才能"帮忙跟进"的，直接退出',
            ].map((tip) => (
              <li key={tip} className="flex items-start gap-2 rounded-md bg-background-50 p-4 text-sm leading-relaxed text-foreground-700">
                <i className="ri-checkbox-circle-line mt-0.5 text-secondary-500"></i>
                {tip}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </SiteLayout>
  );
}